import Button from "core/buttons/button/Button";
import ConsultantCorporateInfoForm from "components/form/consultant/ConsultantCorporateInfoForm";
import ConsultantPersonalInfoForm from "components/form/consultant/ConsultantPersonalInfoForm";
import DoyoubuzzForm from "components/form/consultant/DoyoubuzzForm";
import InfoContext from "pages/InfoContext";
import ResidencePermitForm from "components/form/residence-permit/ResidencePermitForm";
import _ from "lodash";
import { FIELD_REQUIRED } from "constants/popup-codes";
import { FormattedMessage, useIntl } from "react-intl";
import {
  LANG,
  POPUP_STATUS,
  QUERY_STATUS,
  TAG_TYPES,
} from "constants/constants";
import { useContext, useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useUserContext } from "api/user/UserProvider";
import {
  CONSULTANT_FORM_DEFAULT_VALUES,
  CONSULTANT_FORM_LABELS,
  CONSULTANT_FORM_PLACEHOLDERS,
} from "constants/forms";
import colors from "assets/styles/mixins/colors.scss";
import { formatReceivedData } from "utils/staffHelper";

const PersonalConsultant = ({
  readOnly,
  setReadOnly,
  onSubmit,
  initHeader,
  getStatus,
  getCode,
}) => {
  const {
    branches,
    davFunctions,
    lang,
    leavingReasons,
    recruitmentMethodList,
    consultantBySlug,
    userCoefPosition,
    userGrossConventionalMimimum,
    editConsultantStatus,
    nationalitiesList,
    apiErrorResponse,
    isValidationModalOpen,
    usersByIds,
    isVisa,
  } = useContext(InfoContext);
  const intl = useIntl();

  const defaultValues = {
    ...CONSULTANT_FORM_DEFAULT_VALUES,
    email: "",
  };
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    watch,
    clearErrors,
    unregister,
    setError,
  } = useForm({
    defaultValues,
  });
  const [showSection, setShowSection] = useState(false);
  const [cellHighlight, setCellHighlight] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const initializedForm = useRef(false);
  // Synchronous lock: blocks re-entrant submits before React re-renders the disabled button
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    isValidated && errors && setCellHighlight(true);
  }, [errors, isValidated]);

  const apiErrorFieldsRef = useRef([]);
  useEffect(() => {
    if (apiErrorResponse && Object.keys(apiErrorResponse).length > 0) {
      Object.entries(apiErrorResponse).forEach(([field, message]) => {
        setError(field, { type: "manual", message });
      });
      apiErrorFieldsRef.current = Object.keys(apiErrorResponse);
      setReadOnly(false);
    } else if (apiErrorFieldsRef.current.length > 0) {
      apiErrorFieldsRef.current.forEach((field) => clearErrors(field));
      apiErrorFieldsRef.current = [];
    }
  }, [apiErrorResponse]);

  const initForm = () => {
    const defaultKeys = Object.keys(defaultValues);

    const formattedReceivedData = formatReceivedData(
      {
        ...consultantBySlug,
        userCoefPosition,
        userGrossConventionalMimimum,
      },
      {
        branches,
        davFunctions,
        lang,
        leavingReasons,
        recruitmentMethodList,
        users: usersByIds,
        nationalitiesList,
        intl,
      }
    );

    Object.keys(formattedReceivedData).forEach((elm) => {
      const value = formattedReceivedData[elm];
      if (defaultKeys.includes(elm) && value !== undefined) {
        setValue(elm, value);
      }
    });

    if (consultantBySlug?.first_name && consultantBySlug?.last_name) {
      setValue("dyb_showcase_id", [
        {
          value: consultantBySlug?.dyb_showcase_id,
          label: `${consultantBySlug?.first_name} ${consultantBySlug?.last_name}`,
          type: TAG_TYPES.PEOPLE,
        },
      ]);
    }

    if (consultantBySlug?.nationality && nationalitiesList?.length > 0) {
      const nat = nationalitiesList.find(
        (n) => n.id === consultantBySlug.nationality.id
      );
      setValue(
        "nationality",
        nat
          ? [
              {
                label:
                  lang === LANG.FR ? nat.nationality_fr : nat.nationality_eng,
                value: nat.id,
              },
            ]
          : []
      );
    }

    if (userCoefPosition?.length > 0) {
      setValue("syntec_coef_position", userCoefPosition);
    }
  };

  useEffect(() => {
    if (
      !_.isEmpty(consultantBySlug) &&
      !isValidationModalOpen &&
      editConsultantStatus !== QUERY_STATUS.ERROR &&
      editConsultantStatus !== QUERY_STATUS.LOADING
    ) {
      if (!initializedForm.current || readOnly) {
        initForm();
        initializedForm.current = true;
      }
    }
  }, [
    consultantBySlug,
    isValidationModalOpen,
    editConsultantStatus,
    usersByIds,
    readOnly,
  ]);

  const { setApiErrorResponse } = useUserContext();

  const onReset = () => {
    initForm();
    initHeader();
    setReadOnly(true);
    clearErrors();
    setCellHighlight(false);
    setIsValidated(false);
    setApiErrorResponse(null);
    window.scrollTo(0, 0);
    setShowSection(isVisa);
  };

  const onError = (errors) => {
    if (!_.isEmpty(errors)) {
      getStatus(POPUP_STATUS.ERROR);
      getCode(FIELD_REQUIRED);
    }
  };

  const onGuardedSubmit = async (info) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      await onSubmit(info);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return (
    <form onSubmit={handleSubmit(onGuardedSubmit, onError)}>
      <DoyoubuzzForm
        control={control}
        errors={errors}
        readOnly={readOnly}
        register={register}
        setValue={setValue}
        watch={watch}
        cellHighlight={cellHighlight}
      />
      <ConsultantPersonalInfoForm
        control={control}
        errors={errors}
        readOnly={readOnly}
        register={register}
        setValue={setValue}
        watch={watch}
        setError={setError}
        clearErrors={clearErrors}
        unregister={unregister}
        cellHighlight={cellHighlight}
      />
      <br />
      <ResidencePermitForm
        isVisa={isVisa}
        showSection={showSection}
        setShowSection={setShowSection}
        readOnly={readOnly}
        setValue={(field, value) => {
          setValue(field, value);
          clearErrors(field);
        }}
        watch={watch}
        control={control}
        register={register}
        errors={errors}
        labels={CONSULTANT_FORM_LABELS}
        placeholders={CONSULTANT_FORM_PLACEHOLDERS}
      />
      <ConsultantCorporateInfoForm
        setValue={(field, value) => {
          setValue(field, value);
          clearErrors(field);
        }}
        watch={watch}
        control={control}
        register={register}
        unregister={unregister}
        errors={errors}
        readOnly={readOnly}
        cellHighlight={cellHighlight}
      />
      {!readOnly && (
        <div style={{ marginTop: "2rem" }} className="form-buttons">
          <Button
            className="btn-standard btn-outline"
            type="button"
            onClick={onReset}
            id="button-form-reset"
          >
            <FormattedMessage id="cancel" defaultMessage="Annuler" />
          </Button>

          <Button
            color="btn-medium gray-700"
            type="submit"
            id="button-form-validation"
            onClick={() => setIsValidated(true)}
            backgroundColor={
              editConsultantStatus === QUERY_STATUS.LOADING &&
              colors.disabledGrey
            }
            disabled={editConsultantStatus === QUERY_STATUS.LOADING}
          >
            <FormattedMessage
              id="button_form_validation_edition"
              defaultMessage="Valider l'édition"
            />
          </Button>
        </div>
      )}
    </form>
  );
};

export default PersonalConsultant;
